/**
 * Resolve API origin for browser fetch calls.
 * Prefer same-origin `/api/*` on card subdomains and custom hosts so requests
 * stay on the current host (no cross-origin CORS to www).
 */
export function resolveApiOrigin(): string {
  const explicit = String(
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_APP_BASE_URL || '',
  ).replace(/\/+$/, '');
  if (!explicit) return '';
  try {
    if (typeof window === 'undefined') return explicit;
    const current = window.location.origin.replace(/\/+$/, '');
    const expected = new URL(explicit).origin.replace(/\/+$/, '');
    return expected === current ? explicit : '';
  } catch {
    return '';
  }
}

/** Build a full API URL path (always starts with /api/). */
export function apiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const origin = resolveApiOrigin();
  return origin ? `${origin}${normalized}` : normalized;
}

/** Prefix for analytics helpers that append `/ads/...` etc. */
export function resolveApiBase(): string {
  const origin = resolveApiOrigin();
  return origin ? `${origin}/api` : '/api';
}

/** Resolve a media/upload path to an absolute or same-origin URL. */
export function resolveMediaUrl(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return s;
  if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;
  if (s.startsWith('/uploads/') || s.startsWith('/images/') || s.startsWith('/assets/')) return s;
  if (s.startsWith('uploads/')) return `/${s}`;
  const origin = resolveApiOrigin();
  if (origin) return `${origin}/${s.replace(/^\//, '')}`;
  return s.startsWith('/') ? s : `/${s}`;
}
